import React from "react";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";

import classNames from "classnames";

import { ColumnTypeSelectors } from "./ColumnTypeSelectors";

import { useIsAdditionalOfferVisible } from "@utils/offer";
import OfferIcon from "@components/icons/Offer";
import { ChatButton } from "../buttons";
import { ChannelResourceTypeDto } from "@api-types/chat";

export const LiveSportHeader = observer(function LiveSportHeader(props: {
	visible?: boolean;
	eventCount: number;
	isLive?: boolean;
	isHome?: boolean;
	toggleDisabled?: boolean;
	sport: {
		abrv: string;
		name: string;
		id: string;
	};
	onShowHide: (sportId: string) => void;
}) {
	const { t } = useTranslation();

	const isAdditionalOfferVisible = useIsAdditionalOfferVisible();

	const headClasses = classNames("offer__head", {
		"offer--home__head": props.isHome,
		"offer--virtual__head offer__head--sticky":
			!props.isHome && props.isLive,
		"offer__head--bordered": props.toggleDisabled,
		"is-collapsed": !props.visible,
		"is-expanded": props.visible,
	});

	const titleClasses = classNames("offer__head__data offer__title", {
		"offer--home__title": props.isHome,
		"offer--live__title": props.isLive,
	});

	const wrapperClasses = classNames("offer__title__wrapper", {
		"offer--live__title__wrapper": props.isLive,
	});

	const nameClasses = classNames("offer__name", {
		"offer--home__name": props.isHome,
		"offer--live__name": props.isLive,
	});

	const actionClasses = classNames("offer__head__data", {
		offer__actions: !isAdditionalOfferVisible,
	});

	const toggleClasses = classNames("offer__head__data offer__toggle", {
		"offer__toggle--sml": isAdditionalOfferVisible,
	});

	if (props.isHome) {
		return (
			<div className="offer__head offer--home__head">
				<div className="offer__head__row offer--home__head__row">
					<div className="offer--home__info">
						<div className="offer--home__heading">
							<div className="offer--home__title">
								{t(props.sport.name)}
							</div>

							<ChatButton
								channelType={ChannelResourceTypeDto.SPORT}
								resourceId={props.sport.id}
								sportAbrv={props.sport.abrv}
								isHome={props.isHome}
								isInTournamentHeader={props.isLive}
							/>
						</div>
					</div>

					<ColumnTypeSelectors isLive isHome={props.isHome} />

					<div className={toggleClasses} />
				</div>
			</div>
		);
	}

	return (
		<div className={headClasses}>
			<div className="offer__head__row">
				<div className={titleClasses}>
					<div className={wrapperClasses}>
						<div className={nameClasses}>
							{!props.isHome && !props.toggleDisabled ? (
								<span className="offer__icon">
									<OfferIcon
										type="sport"
										sport={props.sport.abrv}
										wrapperClassName="offer__icon__primary u-icon u-icon--lrg"
										className="u-icon--lrg"
									/>
								</span>
							) : null}

							<span className="offer__name__clip">
								{t(props.sport.name)}
							</span>

							{!props.isHome && !props.toggleDisabled ? (
								<EventCount
									eventCount={props.eventCount}
									isLive={props.isLive}
								/>
							) : null}
						</div>
						<div className={actionClasses}>
							<ChatButton
								channelType={ChannelResourceTypeDto.SPORT}
								resourceId={props.sport.id}
								sportAbrv={props.sport.abrv}
								isHome={props.isHome}
								isInTournamentHeader={props.isLive}
							/>
						</div>
					</div>
				</div>

				<ColumnTypeSelectors isLive isHome={props.isHome} />

				<div className={toggleClasses}>
					{!props.isHome && !props.toggleDisabled ? (
						<button
							className="offer__toggle__btn btn btn--square btn--square--xsml btn--ghost"
							type="button"
							onClick={() => props.onShowHide(props.sport.id)}
						>
							<i
								className={classNames(
									"offer__toggle__icon u-icon u-icon--tny",
									{
										"u-icon--caret--down": props.visible,
										"u-icon--caret--right": !props.visible,
									}
								)}
							/>
						</button>
					) : null}
				</div>
			</div>
		</div>
	);
});

const EventCount = observer(function EventCount(props: {
	eventCount: number;
	isLive?: boolean;
}) {
	if (props.eventCount <= 0) {
		return null;
	}

	return (
		<span
			className={classNames(
				"offer__title__counter",
				{
					"offer--live__title__counter": props.isLive,
				},
				"counter counter--base counter--default"
			)}
		>
			{props.eventCount}
		</span>
	);
});
